/**
 * CreateAccountModal.jsx
 * Created by michaelbray on 2/13/18.
 * Updated by Destin Frasier on 1/11/19.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import axios from 'axios';
import validator from 'validator';
import { isNil } from 'lodash';
import * as recaptchav3Lib from  '../../../../components/sharedComponents/recaptcha/recaptchav3Lib';
import * as Icons from '../../../sharedComponents/icons/Icons';
import * as BrandingIcons from '../../../sharedComponents/icons/branding/BrandingIcons';
import Button from '../../../sharedComponents/Button';
import { FlexRow, FlexCol } from '../../../sharedComponents/FlexGrid';
import CreateAccountForm from './CreateAccountForm';
import CreateAccountConfirmation from './CreateAccountConfirmation';

const propTypes = {
    disableAccountFeatures: PropTypes.bool,
    isOpen: PropTypes.bool,
    closeModal: PropTypes.func,
    signIn: PropTypes.func,
    verification: PropTypes.string
};

const defaultProps = {
    disableAccountFeatures: false,
    isOpen: false,
    closeModal: () => { },
    signIn: () => { }
};

const alreadyExistsErr = "An account already exists for this email address";
const challengeRequired = "additional challenge required";

export default class CreateAccountModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            firstName: '',
            lastName: '',
            email: '',
            cellPhone: '',
            errorMessage: '',
            verification: '',
            additionalChallengeRequired: false,
            completed: false
        };

        this.afterOpenModal = this.afterOpenModal.bind(this);
        this.sendAccountConfirmationEmail = this.sendAccountConfirmationEmail.bind(this);
        this.confirmAccount = this.confirmAccount.bind(this);
    }

    componentDidUpdate(prevProps) {
        // You may call setState() immediately in componentDidUpdate() but note that it must be wrapped in a condition
        if (this.props.verification !== prevProps.verification) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                verification: this.props.verification
            });
        }
    }

    afterOpenModal() {
        this.setState({
            firstName: '',
            lastName: '',
            email: '',
            cellPhone: '',
            optIn: true,
            errorMessage: '',
            verification: '',
            additionalChallengeRequired: false,
            completed: false
        });
    }

    sendAccountConfirmationEmail() {
        if (validator.isEmpty(this.state.firstName) ||
            validator.isEmpty(this.state.lastName) ||
            !validator.isEmail(this.state.email) ||
            (validator.isEmpty(this.state.cellPhone) || !validator.isMobilePhone(this.state.cellPhone, 'any'))) {
            this.setState({
                errorMessage: 'All fields are required. Email and mobile phone must be valid.'
            });
        }
        else {
            // To avoid bot detection as much as possible, we are using the consts EAST and WEST
            // to denote which version of CAPTCHA should be used. We immediately begin with V3,
            // and fall back to V2 if V3 fails.
            const regionEast = "EAST"; // Disguised Value for V3
            const regionWest = "WEST"; // Disguised Value for V2
            const action = "initializeRegistration"; // Action being performed

            const url = `${process.env.API}/accounts/registration`;

            if (this.state.additionalChallengeRequired) {
                // If V3 failed, we will attempt V2
                const recaptcha = {
                    section: action, // Action
                    code: this.state.verification, // V2 Captcha Token
                    region: regionWest // Captcha Version (V2)
                };

                axios.post(url, {
                    email: this.state.email,
                    first_name: this.state.firstName,
                    last_name: this.state.lastName,
                    cell_phone: this.state.cellPhone,
                    opt_in: this.state.optIn,
                    system: recaptcha
                })
                .then(() => {
                    this.setState({
                        errorMessage: '',
                        completed: true
                    });
                })
                .catch((e) => {
                    if (e.response.data.error === challengeRequired) {
                        this.setState({
                            additionalChallengeRequired: true
                        });
                    }
                    else {
                        let message = 'An error has occurred, please try again later.';
                        if (e.response.data.error === alreadyExistsErr) {
                            message = "You look familiar! This email already has an account associated with it. Click the link above to log in with this email."
                        }
                        this.setState({
                            errorMessage: message
                        });
                    }
                });
            }
            else {
                // Initial Attempt, use V3
                recaptchav3Lib.getRecaptchaV3(process.env.RECAPTCHAV3_SITE_KEY, action, (res) => {
                    const recaptcha = {
                        section: action, // Action
                        code: !isNil(res.error) ? "" : res.token, // V3 Captcha Token
                        region: regionEast // Captcha Version (V3)
                    };

                    axios.post(url, {
                        email: this.state.email,
                        first_name: this.state.firstName,
                        last_name: this.state.lastName,
                        cell_phone: this.state.cellPhone,
                        opt_in: this.state.optIn,
                        system: recaptcha
                    })
                    .then(() => {
                        this.setState({
                            errorMessage: '',
                            completed: true
                        });
                    })
                    .catch((e) => {
                        if (e.response.data.error === challengeRequired) {
                            this.setState({
                                additionalChallengeRequired: true
                            });
                        }
                        else {
                            let message = 'An error has occurred, please try again later.';
                            if (e.response.data.error === alreadyExistsErr) {
                                message = "You look familiar! This email already has an account associated with it. Click the link above to log in with this email."
                            }
                            this.setState({
                                errorMessage: message
                            });
                        }
                    });
                });
            }
        }
    }

    confirmAccount(account) {
        this.setState({
            email: account.email,
            firstName: account.firstName,
            lastName: account.lastName,
            cellPhone: account.cellPhone,
            optIn: account.optIn
        }, () => {
            this.sendAccountConfirmationEmail();
        });
    }

    render() {
        let errorText = null;

        if (this.state.errorMessage) {
            errorText = this.state.errorMessage;
        }

        let content = null;

        if (this.state.completed) {
            content = (
                <CreateAccountConfirmation
                    email={this.state.email}
                    sendAccountConfirmationEmail={this.sendAccountConfirmationEmail} />
            );
        }
        else {
            content = (
                <CreateAccountForm
                    disableAccountFeatures={this.props.disableAccountFeatures}
                    disableSignUpButton={this.state.additionalChallengeRequired && this.state.verification === ''}
                    errorText={errorText}
                    signIn={this.props.signIn}
                    confirmAccount={this.confirmAccount}
                    additionalChallengeRequired={this.state.additionalChallengeRequired} />
            );
        }

        return (
            <Modal
                role="complementary"
                isOpen={this.props.isOpen}
                onAfterOpen={this.afterOpenModal}
                onRequestClose={this.props.closeModal}
                className={{
                    base: 'rec-modal-dialog-base',
                    afterOpen: 'rec-modal-dialog-after-open-md rec-acct-modal rec-acct-create-modal',
                    beforeClose: 'rec-modal-dialog-before-close'
                }}
                overlayClassName={{
                    base: 'rec-overlay-base',
                    afterOpen: 'rec-overlay-after-open',
                    beforeClose: 'rec-overlay-before-close'
                }}
                aria={{
                    labelledby: "createAccountModalHeading"
                }}
                ariaHideApp={false}>
                <div className="rec-modal-content-wrap">
                    <FlexRow justifyContent="space-between">
                        <FlexCol className="rec-modal-logo">
                            <BrandingIcons.Logo />
                        </FlexCol>
                        <FlexCol xs="variable">
                            <Button
                                appearance="subtle"
                                iconBeforeElement={<Icons.IconClose />}
                                screenReaderTextBefore="Close Log In as Guest Dialog"
                                onClick={this.props.closeModal}
                                className="rec-close-modal" />
                        </FlexCol>
                    </FlexRow>
                    {content}
                </div>
            </Modal>
        );
    }
}

CreateAccountModal.propTypes = propTypes;
CreateAccountModal.defaultProps = defaultProps;
